import React, { useState } from "react";
import { FaWhatsapp, FaPhoneAlt, FaPaperPlane, FaTimes, FaCircle } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";

const WhatsAppButton = () => {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [chat, setChat] = useState([]);

  const phoneNumber = "9910680809";
  const contactButtonVisibility = ["/book-demo-section", "/book-demo-section/from1"].includes(pathname)
    ? "hidden md:flex"
    : "flex";

  const handleSend = () => {
    if (message.trim() === "") return;

    const newMsg = { text: message, type: "user" };
    setChat([...chat, newMsg]);

    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
    setMessage("");
    setIsOpen(false);
  };

  return (
    <>
      {/* Phone Button */}
      <motion.a
        href={`tel:${phoneNumber}`}
        className={`fixed bottom-6 left-6 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white rounded-full p-4 shadow-xl items-center justify-center z-50 ${contactButtonVisibility}`}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
        animate={{ y: [0, -5, 0] }}
        transition={{ repeat: Infinity, duration: 1.3 }}
      >
        <FaPhoneAlt size={22} />
      </motion.a>

      {/* WhatsApp Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 left-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-full p-4 shadow-xl items-center justify-center z-[999] ${contactButtonVisibility}`}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
        animate={{ y: [0, -5, 0] }}
        transition={{ repeat: Infinity, duration: 1.3 }}
      >
        {isOpen ? <FaTimes size={24} /> : <FaWhatsapp size={28} />}
      </motion.button>

      {/* WhatsApp Chat Popup */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.6, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.6, y: 40 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className={`fixed bottom-40 left-6 w-80 md:w-96 h-[480px] flex-col bg-white rounded-3xl overflow-hidden shadow-2xl z-[99999] ${contactButtonVisibility}`}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <img
                    src="https://cdn-icons-png.flaticon.com/512/149/149071.png"
                    className="w-12 h-12 rounded-full border-2 border-white"
                    alt="support"
                  />
                  <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-400 border-2 border-white rounded-full"></span>
                </div>
                <div>
                  <p className="font-bold text-lg">sapeagleERP</p>
                  <p className="text-xs text-white/80 flex items-center gap-1">
                    <FaCircle className="text-[8px]" /> Online
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Welcome Message */}
            <div className="bg-green-50 p-4 text-center border-b border-green-100">
              <p className="text-gray-700 text-sm">
                Welcome to <span className="font-bold text-green-700">sapeagleERP</span>
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Click below to start a conversation
              </p>
            </div>

            {/* Chat Area */}
            <div className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-gray-50 to-white">
              {/* Initial Message */}
              {chat.length === 0 && (
                <div className="flex justify-start mb-4">
                  <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-tl-none max-w-[85%] shadow-sm">
                    <p className="text-gray-800 text-sm">
                      👋 Hello! How can we help you today?
                    </p>
                    <p className="text-gray-400 text-xs mt-1">We typically reply in a few minutes</p>
                  </div>
                </div>
              )}

              {/* Chat Messages */}
              {chat.map((msg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: msg.type === "user" ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`mb-3 flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`px-4 py-2.5 rounded-2xl max-w-[80%] text-sm shadow-md ${
                      msg.type === "user"
                        ? "bg-gradient-to-r from-green-500 to-green-600 text-white rounded-br-none"
                        : "bg-white text-gray-800 rounded-bl-none border border-gray-200"
                    }`}
                  >
                    {msg.text}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Quick Reply Buttons */}
            <div className="px-3 py-2 bg-gray-50 border-t flex gap-2 overflow-x-auto">
              {[
                "I need a demo",
                "Pricing inquiry",
                "Feature request",
                "Technical support"
              ].map((quickReply, index) => (
                <button
                  key={index}
                  onClick={() => setMessage(quickReply)}
                  className="whitespace-nowrap px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs text-gray-600 hover:bg-green-50 hover:border-green-300 hover:text-green-600 transition-colors"
                >
                  {quickReply}
                </button>
              ))}
            </div>

            {/* Input Area */}
            <div className="p-3 bg-white border-t flex items-center gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="w-full border border-gray-200 rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 transition-all"
                  onKeyPress={(e) => e.key === "Enter" && handleSend()}
                />
              </div>
              <motion.button
                onClick={handleSend}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                disabled={!message.trim()}
                className={`p-3 rounded-2xl shadow-md transition-all ${
                  message.trim()
                    ? "bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                <FaPaperPlane size={18} />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default WhatsAppButton;
